import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Toastify, toastGetError } from "../../../components/Toaster";

/**
 * OAuth / OIDC Integrations (Core canonical /v2/manage/integrations/oauth, PCA-19295).
 *
 * OAuth and OIDC share a single Core surface; Core stores no OAuth-vs-OIDC
 * discriminator, so the distinction is a UI-only preset. Records are keyed by `Id`, an
 * opaque tenant-unique identifier Core generates at creation (its AppName) and never
 * reuses; `DisplayName` is the operator-facing label, immutable after create and NOT unique. `ClientSecret` is returned by Core exactly once — on create
 * and on credential rotation — and is never present on list/detail reads.
 */
export interface OAuthIntegrationSchema {
  Id?: string;
  DisplayName: string;
  ClientId?: string;
  ClientSecret?: string;
  RedirectURIs: string[];
  AllowedScopes?: string[];
  GrantTypes: string[];
  AccessTokenMappingTemplate?: string;
  IdTokenMappingTemplate?: string;
  AccessTokenTTL?: number;
  IDTokenTTL?: number;
  RefreshTokenTTL?: number;
  EnablePKCE?: boolean;
  IsPrebuiltIntegration?: boolean;
  IntegrationType?: string | null;
  TokenAuthMethod?: string;
  SubjectType?: string;
  SectorIdentifierURI?: string;

  /**
   * CONTRACT ASSUMPTION — delivered by a separate Core task, not present on
   * OAuthIntegrationConfig today.
   *
   * Expected to mirror the Applications (`OAuthConfig`) semantics exactly:
   *  - `DefaultWorkflow`: name of the orchestration workflow to run at authorize time when the
   *    request does not name one. Value is the workflow Name, not an id. Update is partial-safe
   *    (omitted => preserved, explicit "" => cleared), which is what lets this form omit the
   *    field entirely when Identity Orchestration is unavailable.
   *  - `Connections`: which sign-in methods the integration accepts, same structure as
   *    `OAuthConfig.Connections`. Absent on records created before the feature.
   */
  DefaultWorkflow?: string;
  Connections?: any;
}

/**
 * Body for a prebuilt (catalogue-template) create.
 *
 * `IntegrationType` is the catalogue entry's exact `name`. Every other key is a field the
 * selected template declared, keyed by its exact `fieldName` — generated from the catalogue,
 * never from a frontend allowlist. Core maps `fieldName` straight onto its wrapper request
 * struct and silently drops keys it does not know, so casing and spelling are passed through
 * untouched.
 *
 * Scope of "no frontend change": a new seeded vendor needs none PROVIDED its fields are ones
 * the protocol's backend wrapper request contract already supports (today DisplayName and
 * RedirectURI). That contract is the boundary — introducing a brand-new field such as
 * TenantDomain requires Core support first, even though this renderer stays generic.
 */
export interface OAuthIntegrationWrapperPayload {
  IntegrationType: string;
  [fieldName: string]: string;
}

export interface RevealedCredentials {
  ClientId?: string;
  ClientSecret?: string;
}

export interface OAuthIntegrationsState {
  Data: OAuthIntegrationSchema[];
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  errorMessage: string | null;

  // One flag per in-flight operation, matching the convention across redux/features. Outcome
  // is carried by the shared isSuccess / isError / errorMessage above: a consumer keys its
  // effect on the operation flag it started and reads isSuccess, so a list load cannot be
  // mistaken for a create.
  isAdding: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isRotating: boolean;

  // Backend validation errors Core attributed unambiguously to a request field, keyed by that
  // field's name. Rendered inline by the form; anything Core does not tie to a field falls
  // back to errorMessage and the usual global toast.
  fieldErrors: Record<string, string> | null;

  // One-time plaintext secret surfaced after a create or a rotation. Held so the
  // credentials card can reveal it inline, then cleared via clearRevealedCredentials.
  revealedCredentials: RevealedCredentials | null;

  // The full record returned by the last create, used to transition the create
  // page into the edit page in place (no navigation, no popup). Secret-stripped;
  // the plaintext secret lives in revealedCredentials.
  lastSaved: OAuthIntegrationSchema | null;
}

const initialState: OAuthIntegrationsState = {
  Data: [],
  isLoading: false,
  isSuccess: false,
  isError: false,
  errorMessage: null,

  isAdding: false,
  isUpdating: false,
  isDeleting: false,
  isRotating: false,
  fieldErrors: null,

  revealedCredentials: null,
  lastSaved: null
};

// Never keep a plaintext secret in the list cache — strip it before storing.
const stripSecret = (integration: OAuthIntegrationSchema): OAuthIntegrationSchema => {
  const { ClientSecret, ...rest } = integration;
  return rest;
};

export const OAuthIntegrationsSlice = createSlice({
  name: "oauthIntegrations",
  initialState,
  reducers: {
    getOAuthIntegrationsRequest(state) {
      state.isLoading = true;
      state.isSuccess = false;
      state.isError = false;
      state.errorMessage = null;
    },
    getOAuthIntegrationsSuccess(state, action: PayloadAction<OAuthIntegrationSchema[]>) {
      state.isLoading = false;
      state.isSuccess = true;
      state.Data = (action.payload || []).map(stripSecret);
    },
    getOAuthIntegrationsFailure(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.isError = true;
      state.errorMessage = action.payload;
      // A load failure routes through toastGetError so a missing read permission is not toasted:
      // the page already renders its restricted state, and every list on it would otherwise raise
      // the same "not authorized" message at once. Mutation failures below stay on Toastify — those
      // are user-initiated and the user needs to be told the action did not happen.
      toastGetError(action.payload);
    },

    createOAuthIntegrationRequest(state, _action: PayloadAction<OAuthIntegrationSchema>) {
      state.isAdding = true;
      state.isSuccess = false;
      state.isError = false;
      state.errorMessage = null;
    },
    createOAuthIntegrationSuccess(state, action: PayloadAction<OAuthIntegrationSchema>) {
      state.isAdding = false;
      state.isUpdating = false;
      state.isSuccess = true;
      state.Data = [...state.Data, stripSecret(action.payload)];
      state.lastSaved = stripSecret(action.payload);
      state.revealedCredentials = {
        ClientId: action.payload.ClientId,
        ClientSecret: action.payload.ClientSecret
      };
    },
    createOAuthIntegrationFailure(state, action: PayloadAction<string>) {
      state.isAdding = false;
      state.isUpdating = false;
      state.isError = true;
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    },

    // Prebuilt create from a catalogue template. Shares createOAuthIntegrationSuccess with
    // the custom create path so the one-time secret, the edit-in-place transition and the
    // list update behave identically; failures go to createOAuthIntegrationWrapperFailure
    // below, which additionally carries per-field errors.
    createOAuthIntegrationWrapperRequest(state, _action: PayloadAction<OAuthIntegrationWrapperPayload>) {
      state.isAdding = true;
      state.isSuccess = false;
      state.isError = false;
      state.errorMessage = null;
      state.fieldErrors = null;
    },

    // Wrapper-create failure. Unlike the custom-create failure this can carry per-field
    // errors; when it does the form renders them inline and no toast is raised, so the same
    // problem is not reported twice. Errors Core does not attribute to a field (tenant
    // integration limit, feature disabled, permission, unexpected failure) arrive with no
    // fieldErrors and fall through to the existing global toast.
    createOAuthIntegrationWrapperFailure(state, action: PayloadAction<{ message: string; fieldErrors?: Record<string, string> | null }>) {
      state.isAdding = false;
      state.isUpdating = false;
      state.isError = true;
      state.errorMessage = action.payload.message;
      state.fieldErrors = action.payload.fieldErrors || null;
      if (!action.payload.fieldErrors) {
        Toastify("error", action.payload.message);
      }
    },

    // payload.Id is the immutable masked id used in the URL; the remaining fields
    // form the update body. Core ignores immutable/server-owned fields on update.
    updateOAuthIntegrationRequest(state, _action: PayloadAction<OAuthIntegrationSchema>) {
      state.isUpdating = true;
      state.isSuccess = false;
      state.isError = false;
      state.errorMessage = null;
    },
    updateOAuthIntegrationSuccess(state, action: PayloadAction<OAuthIntegrationSchema>) {
      state.isAdding = false;
      state.isUpdating = false;
      state.isSuccess = true;
      const updated = stripSecret(action.payload);
      state.Data = state.Data.map((item) => (item.Id === updated.Id ? updated : item));
      // Keep lastSaved in step after a create->edit-in-place transition; the form reads
      // `record` from it, so leaving it at the create-time values would make Reset revert a
      // saved update and make dirty tracking compare against stale data.
      if (state.lastSaved && state.lastSaved.Id === updated.Id) {
        state.lastSaved = updated;
      }
    },
    updateOAuthIntegrationFailure(state, action: PayloadAction<string>) {
      state.isAdding = false;
      state.isUpdating = false;
      state.isError = true;
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    },

    deleteOAuthIntegrationRequest(state, _action: PayloadAction<{ Id: string; DisplayName?: string }>) {
      state.isDeleting = true;
      state.isSuccess = false;
      state.isError = false;
      state.errorMessage = null;
    },
    deleteOAuthIntegrationSuccess(state, action: PayloadAction<{ Id: string }>) {
      state.isDeleting = false;
      state.isSuccess = true;
      state.Data = state.Data.filter((item) => item.Id !== action.payload.Id);
    },
    deleteOAuthIntegrationFailure(state, action: PayloadAction<string>) {
      state.isDeleting = false;
      state.isError = true;
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    },

    rotateOAuthIntegrationCredentialsRequest(state, _action: PayloadAction<{ Id: string }>) {
      state.isRotating = true;
      state.isSuccess = false;
      state.isError = false;
      state.errorMessage = null;
    },
    rotateOAuthIntegrationCredentialsSuccess(state, action: PayloadAction<RevealedCredentials>) {
      state.isRotating = false;
      state.isSuccess = true;
      state.revealedCredentials = {
        ClientId: action.payload.ClientId,
        ClientSecret: action.payload.ClientSecret
      };
    },
    rotateOAuthIntegrationCredentialsFailure(state, action: PayloadAction<string>) {
      state.isRotating = false;
      state.isError = true;
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    },

    clearRevealedCredentials(state) {
      state.revealedCredentials = null;
    },

    clearLastSaved(state) {
      state.lastSaved = null;
    },

    // Clear transient mutation flags (used when opening/closing the form) without
    // discarding the loaded list.
    resetOAuthIntegrationMutationState(state) {
      state.isAdding = false;
      state.isUpdating = false;
      state.isDeleting = false;
      state.isRotating = false;
      state.isSuccess = false;
      state.isError = false;
      state.errorMessage = null;
      state.fieldErrors = null;
      state.revealedCredentials = null;
      state.lastSaved = null;
    }
  }
});

export const {
  getOAuthIntegrationsRequest,
  getOAuthIntegrationsSuccess,
  getOAuthIntegrationsFailure,
  createOAuthIntegrationRequest,
  createOAuthIntegrationSuccess,
  createOAuthIntegrationFailure,
  createOAuthIntegrationWrapperRequest,
  createOAuthIntegrationWrapperFailure,
  updateOAuthIntegrationRequest,
  updateOAuthIntegrationSuccess,
  updateOAuthIntegrationFailure,
  deleteOAuthIntegrationRequest,
  deleteOAuthIntegrationSuccess,
  deleteOAuthIntegrationFailure,
  rotateOAuthIntegrationCredentialsRequest,
  rotateOAuthIntegrationCredentialsSuccess,
  rotateOAuthIntegrationCredentialsFailure,
  clearRevealedCredentials,
  clearLastSaved,
  resetOAuthIntegrationMutationState
} = OAuthIntegrationsSlice.actions;

export default OAuthIntegrationsSlice.reducer;
